import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PlanCard from './PlanCard';
import CurrentSubscriptionCard from './CurrentSubscriptionCard';
import '../../styles/SubscriptionTab.css';
import {useSubscription} from "../../context/SubscriptionContext";
import useSubscriptionApi from "../../hooks/useSubscriptionApi";
import {SUBSCRIPTION_STATUS} from "../../utils/constants";

/**
 * SubscriptionTab Component
 *
 * Subscription management component designed to work within a tab layout.
 * Unlike SubscriptionPlans, this doesn't have its own page wrapper/header.
 * Used in TrainerAccount tabs.
 */
const SubscriptionTab = () => {
    const navigate = useNavigate();

    // Context
    const {
        subscriptionData,
        refreshSubscriptionStatus,
        getSubscriptionStatusText,
        formatDate,
        isInGracePeriod,
        canStartTrial
    } = useSubscription();

    // API hook
    const {
        loading: apiLoading,
        error: apiError,
        clearError,
        fetchPlans,
        createCheckoutSession,
        changePlan,
        openCustomerPortal,
        startTrial
    } = useSubscriptionApi();

    // Local state
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingPlan, setProcessingPlan] = useState(null);
    const [isStartingTrial, setIsStartingTrial] = useState(false);

    // Sync API errors to local error state
    useEffect(() => {
        if (apiError) {
            setError(apiError);
        }
    }, [apiError]);

    // Fetch plans on mount
    useEffect(() => {
        loadPlans();
    }, []);

    /**
     * Load subscription plans
     */
    const loadPlans = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const fetchedPlans = await fetchPlans();
            setPlans(fetchedPlans);
        } catch (err) {
            // Error already handled by hook
        } finally {
            setLoading(false);
        }
    }, [fetchPlans]);

    /**
     * Handle plan selection
     */
    const handleSelectPlan = useCallback(async (plan) => {
        try {
            setProcessingPlan(plan.id);
            setError(null);

            const isActivePlan = subscriptionData?.status === SUBSCRIPTION_STATUS.ACTIVE;
            const isDifferentPlan = subscriptionData?.priceId !== plan.priceId;

            // If user has active subscription, this is a plan change
            if (isActivePlan && isDifferentPlan) {
                const confirmChange = window.confirm(
                    `Czy chcesz zmienić plan na "${plan.name}"? Zmiana zostanie rozliczona proporcjonalnie.`
                );

                if (!confirmChange) {
                    setProcessingPlan(null);
                    return;
                }

                await changePlan(plan.priceId);
                alert('Plan został zmieniony pomyślnie!');
                await refreshSubscriptionStatus();
            } else {
                // New subscription - create checkout session
                await createCheckoutSession(plan.priceId);
            }
        } catch (err) {
            setError(err.message || 'Nie udało się przetworzyć żądania.');
        } finally {
            setProcessingPlan(null);
        }
    }, [subscriptionData, changePlan, createCheckoutSession, refreshSubscriptionStatus]);

    /**
     * Handle starting trial
     */
    const handleStartTrial = useCallback(async () => {
        try {
            setIsStartingTrial(true);
            setError(null);

            await startTrial();
            await refreshSubscriptionStatus();
            alert('Okres próbny został aktywowany!');
        } catch (err) {
            setError(err.message || 'Nie udało się rozpocząć okresu próbnego.');
        } finally {
            setIsStartingTrial(false);
        }
    }, [startTrial, refreshSubscriptionStatus]);

    /**
     * Handle opening customer portal
     */
    const handleOpenPortal = useCallback(async () => {
        try {
            setLoading(true);
            await openCustomerPortal();
        } catch (err) {
            setError('Nie udało się otworzyć portalu płatności.');
        } finally {
            setLoading(false);
        }
    }, [openCustomerPortal]);

    /**
     * Check if plan is the current active plan
     */
    const isCurrentPlan = (plan) => {
        return (
            subscriptionData?.priceId === plan.priceId &&
            subscriptionData?.status === SUBSCRIPTION_STATUS.ACTIVE
        );
    };

    /**
     * Clear error message
     */
    const handleClearError = () => {
        setError(null);
        clearError();
    };

    const hasActiveSubscription = subscriptionData?.status === SUBSCRIPTION_STATUS.ACTIVE;
    const showTrialOption = canStartTrial() && !hasActiveSubscription;

    // Loading state
    if (loading && plans.length === 0) {
        return (
            <div className="subscription-tab">
                <div className="subscription-tab__loading">
                    <div className="subscription-tab__spinner"></div>
                    <p>Ładowanie danych subskrypcji...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="subscription-tab">
            {/* Section Header */}
            <div className="subscription-tab__header">
                <h2>Zarządzanie subskrypcją</h2>
                <p>Przeglądaj i zarządzaj swoim planem subskrypcji</p>
            </div>

            {/* Error banner */}
            {error && (
                <div className="subscription-tab__error">
                    <p>{error}</p>
                    <button onClick={handleClearError}>×</button>
                </div>
            )}

            {/* Current subscription status */}
            <CurrentSubscriptionCard
                subscriptionData={subscriptionData}
                isInGracePeriod={isInGracePeriod()}
                onOpenPortal={handleOpenPortal}
                getStatusText={getSubscriptionStatusText}
                formatDate={formatDate}
            />

            {/* Trial offer for new users */}
            {showTrialOption && (
                <div className="subscription-tab__trial-offer">
                    <div className="trial-offer__content">
                        <div className="trial-offer__icon">🎁</div>
                        <div className="trial-offer__text">
                            <h3>Bezpłatny okres próbny</h3>
                            <p>
                                Wypróbuj wszystkie funkcje za darmo przez 30 dni.
                                Bez zobowiązań - możesz anulować kiedy chcesz.
                            </p>
                        </div>
                        <button
                            className="trial-offer__button"
                            onClick={handleStartTrial}
                            disabled={isStartingTrial}
                        >
                            {isStartingTrial ? 'Aktywowanie...' : 'Rozpocznij za darmo'}
                        </button>
                    </div>
                </div>
            )}

            {/* Plans section */}
            <div className="subscription-tab__plans-section">
                <h3>Dostępne plany</h3>

                {plans.length === 0 ? (
                    <div className="subscription-tab__empty">
                        <p>Brak dostępnych planów.</p>
                        <button onClick={loadPlans}>Odśwież</button>
                    </div>
                ) : (
                    <div className="subscription-tab__plans-grid">
                        {plans.map((plan) => (
                            <PlanCard
                                key={plan.id}
                                plan={plan}
                                isCurrentPlan={isCurrentPlan(plan)}
                                isProcessing={processingPlan === plan.id}
                                hasActiveSubscription={hasActiveSubscription}
                                onSelect={handleSelectPlan}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Help section */}
            <div className="subscription-tab__help">
                <p>
                    Masz pytania dotyczące subskrypcji?{' '}
                    <a href="mailto:help@petboard.pl">Skontaktuj się z nami</a>
                </p>
            </div>
        </div>
    );
};

export default SubscriptionTab;